import { useEffect, useState } from "react";
import { Star, Loader2, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { StarRating } from "@/components/store/StarRating";
import { useLanguage } from "@/context/LanguageContext";
import api, { apiError } from "@/lib/api";

export default function StoreReviews() {
  const { t } = useLanguage();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(0);
  const [form, setForm] = useState({ customer_name: "", location: "", rating: 5, title: "", text: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = () => {
    setLoading(true);
    api.get("/reviews").then((r) => setReviews(r.data.reviews)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rating) { toast.error(t("Please select a rating")); return; }
    setSaving(true);
    try {
      await api.post("/reviews", form);
      toast.success(t("Thank you! Your review will be published once approved."));
      setForm({ customer_name: "", location: "", rating: 5, title: "", text: "" });
      setOpen(false);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="reviews-page">
      <div className="border-b border-border bg-secondary/30">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-16 text-center">
          <p className="overline text-gold mb-3">{t("Client Voices")}</p>
          <h1 className="font-serif text-5xl sm:text-6xl font-light tracking-tight">{t("Reviews")}</h1>
          <p className="text-muted-foreground mt-4 max-w-xl mx-auto">{t("What our clients say")}</p>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-none h-12 px-10 mt-8" data-testid="open-review-form-btn">{t("Leave a Review")}</Button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-md" data-testid="review-form">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">{t("Your Review")}</DialogTitle>
                <DialogDescription className="sr-only">Submit your store review</DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <Label className="overline text-muted-foreground">{t("Your Rating")}</Label>
                  <div className="flex gap-1 mt-2" data-testid="rating-picker">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} type="button" onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => setForm((f) => ({ ...f, rating: n }))} data-testid={`review-rating-${n}`} aria-label={`${n} stars`} className="p-1">
                        <Star className={`h-7 w-7 transition-colors ${n <= (hover || form.rating) ? "fill-gold text-gold" : "text-muted-foreground/40"}`} />
                      </button>
                    ))}
                  </div>
                </div>
                <div><Label className="overline text-muted-foreground" htmlFor="r-name">{t("Your Name")}</Label><Input id="r-name" required value={form.customer_name} onChange={set("customer_name")} className="rounded-none mt-1.5" data-testid="review-name" /></div>
                <div><Label className="overline text-muted-foreground" htmlFor="r-loc">{t("Location (optional)")}</Label><Input id="r-loc" value={form.location} onChange={set("location")} className="rounded-none mt-1.5" placeholder="Chicago, IL" data-testid="review-location" /></div>
                <div><Label className="overline text-muted-foreground" htmlFor="r-title">{t("Title (optional)")}</Label><Input id="r-title" value={form.title} onChange={set("title")} className="rounded-none mt-1.5" data-testid="review-title" /></div>
                <div><Label className="overline text-muted-foreground" htmlFor="r-msg">{t("Your Message")}</Label><Textarea id="r-msg" required rows={4} value={form.text} onChange={set("text")} className="rounded-none mt-1.5 resize-none" data-testid="review-message" /></div>
                <Button type="submit" className="w-full rounded-none h-12" disabled={saving} data-testid="submit-review-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Submit Review")}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-16">
        {loading ? (
          <div className="py-24 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
        ) : reviews.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground">{t("Be the first to leave a review.")}</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((r) => (
              <div key={r.id} className="border border-border p-8" data-testid={`review-item-${r.id}`}>
                <div className="flex items-center justify-between">
                  <StarRating rating={r.rating} />
                  <Quote className="h-4 w-4 text-gold/40" />
                </div>
                {r.title && <h3 className="font-serif text-xl mt-4">{r.title}</h3>}
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{r.text}</p>
                <div className="mt-6 pt-4 border-t border-border">
                  <p className="text-sm font-medium">{r.customer_name}</p>
                  {r.location && <p className="text-xs text-muted-foreground">{r.location}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
